import { ReactNode, type FC } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  SplitFactoryProvider as UntypedSplitFactoryProvider,
  useSplitTreatments,
  type ISplitFactoryProps,
} from '@splitsoftware/splitio-react';
import { getDemoFeatureTreatments } from '@/demo/demoHandlers';
import { isDemoModeAllowed } from '@/demo/demoMode';
import enterpriseFeatureFlags, { Features as EnterpriseFeatures } from '@/enterprise/features';
import FullPageLoader from '@/shared/components/FullPageLoader';
import useUserDetails from '@/shared/data/useUserDetails';
import { getTokenContent } from '@/utils/token';

// @splitsoftware/splitio-react@1.12.0's types predate React 19's stricter ReactNode/ReactPortal
// typing and haven't been updated; cast to bridge the gap without changing runtime behavior.
const SplitFactoryProvider = UntypedSplitFactoryProvider as unknown as FC<ISplitFactoryProps>;

export const DEFAULT_SPLIT_KEY = 'no-divisions-user';

export const Features = {
  ...EnterpriseFeatures,
} as const;

const features = {
  ...enterpriseFeatureFlags,
};

export type Feature = keyof typeof Features;

export function FeaturesProviderWithSplitKey({ children, splitKey }: { children: ReactNode; splitKey: string }) {
  const config: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: import.meta.env.VITE_SPLIT_IO_KEY ?? 'localhost',
      key: splitKey,
    },
    impressionListener: {
      logImpression(impressionData) {
        datadogRum.addFeatureFlagEvaluation(impressionData.impression.feature, impressionData.impression.treatment);
      },
    },
    features,
    startup: {
      readyTimeout: 3,
      requestTimeoutBeforeReady: 3,
      retriesOnFailureBeforeReady: 5,
    },
  };

  return (
    <SplitFactoryProvider config={config} updateOnSdkTimedout updateOnSdkUpdate>
      {({ isReady, isTimedout }) => {
        // loading
        if (!isReady && !isTimedout) {
          return <FullPageLoader />;
        }

        // good to go
        return <div>{children}</div>;
      }}
    </SplitFactoryProvider>
  );
}

function getFeatureTreatment<T = Record<string, unknown>>(
  treatments: SplitIO.TreatmentsWithConfig,
  featureName: Feature
) {
  const feature = treatments[featureName];

  let treatment = feature.treatment;

  /**
   * if split fails to load and does not return a treatment, "control" will be returned
   * fallback to utilizing treatment override by env if defined
   */
  if (treatment === 'control' && features[featureName]) {
    treatment = features[featureName];
  }

  /**
   * In demo mode the real Split SDK still serves treatments from the demo environment, but flags
   * configured in the demo config are forced on top of that. Overrides are built during demo handler
   * building (buildDemoFeatureTreatments in loadDemoHandlers); only overridden flags are affected,
   * everything else keeps its real treatment. Gated by isDemoModeAllowed() so uat/prod skip the
   * lookup entirely on every treatment read.
   */
  if (isDemoModeAllowed()) {
    const demoTreatment = getDemoFeatureTreatments()[featureName];
    if (typeof demoTreatment === 'string') {
      return [demoTreatment === 'on', undefined] as const;
    }
  }

  return [treatment === 'on', feature.config ? (JSON.parse(feature.config) as T) : undefined] as const;
}

function useAccountSplitKey() {
  const { data: accountId } = useUserDetails(({ me }) => me?.account?.id);
  return accountId ?? DEFAULT_SPLIT_KEY;
}

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const splitKey = useAccountSplitKey();
  return <FeaturesProviderWithSplitKey splitKey={splitKey}>{children}</FeaturesProviderWithSplitKey>;
}

/**
 * Check if a Split treatment's value is "on".
 * @returns A tuple containing whether the treatment is on and the config object, or undefined if it exists.
 */
export default function useFeature(featureName: Feature, attributes?: SplitIO.Attributes) {
  const splitKey = useAccountSplitKey();

  const { treatments } = useSplitTreatments({
    names: [featureName],
    attributes: { ...attributes, trafficType: 'account' },
    splitKey,
  });

  return getFeatureTreatment(treatments, featureName);
}

export function useFeatureForUser(featureName: Feature, attributes?: SplitIO.Attributes) {
  const token = getTokenContent();
  const splitKey = token?.payload?.impersonationSubject?.uuid ?? token?.payload?.user?.uuid ?? DEFAULT_SPLIT_KEY;

  const { treatments } = useSplitTreatments({
    names: [featureName],
    attributes: { ...attributes, trafficType: 'user' },
    splitKey,
  });

  return getFeatureTreatment(treatments, featureName);
}
